import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Types ============

export interface ResourceSkill {
  id: string;
  code: string;
  name: string;
  description: string | null;
  colorBg: string;
  colorText: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use ResourceSkill instead */
export type VcfSkill = ResourceSkill;

interface SkillsResponse {
  skills: ResourceSkill[];
}

interface SkillResponse {
  skill: ResourceSkill;
}

interface CreateSkillInput {
  code: string;
  name: string;
  description?: string;
  colorBg?: string;
  colorText?: string;
  sortOrder?: number;
}

interface UpdateSkillInput {
  code?: string;
  name?: string;
  description?: string | null;
  colorBg?: string;
  colorText?: string;
  sortOrder?: number;
  isActive?: boolean;
}

// ============ Hooks ============

export function useSkills(includeInactive = false) {
  return useQuery({
    queryKey: ['skills', { includeInactive }],
    queryFn: () =>
      api.get<SkillsResponse>('/vcf/skills', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSkillInput) => api.post<SkillResponse>('/vcf/skills', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}

export function useUpdateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateSkillInput) =>
      api.patch<SkillResponse>(`/vcf/skills/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}

export function useDeleteSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/vcf/skills/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}
